/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback, useRef } from '@web-stories-wp/react';
import getAllTemplates from '@web-stories-wp/templates';

/**
 * Internal dependencies
 */
import { useConfig } from '../config';
import Context from './context';
import { removeImagesFromPageTemplates } from './utils';

function APIProvider({ children }) {
  const {
    api: {
      stories,
      media,
      hotlink,
      link,
      users,
      currentUser,
      pageTemplates: customPageTemplates,
      taxonomies,
    },
    apiCallbacks,
    encodeMarkup,
    cdnURL,
    postType,
  } = useConfig();

  const {
    getStoryById,
    getDemoStoryById,
    saveStoryById,
    autoSaveById,
    getMedia,
    uploadMedia,
    uploadMediaFromBase64String,
    updateMedia,
    deleteMedia,
    deleteExternalAsset,
    getLinkMetadata,
    getAuthors,
    getCurrentUser,
    updateCurrentUser,
    getCustomPageTemplates,
    addPageTemplate,
    deletePageTemplate,
    updatePageTemplateMetadata,
    updatePageTemplateOrder,
    updatePageTemplatePin,
    movePageTemplate,
    getHotlinkInfo,
    getTaxonomies,
    getTaxonomyTerm,
    createTaxonomyTerm,
    savePages,
    getMediaFromProvider,
    getMediaDetailsFromProvider,
    registerUsage,
    download3pAsset,
    getExternalAssets,
    getFonts,
    getShapes,
    getStickers,
    getTextSets,
    getStudioAnimations,
    getStudioAnimation,
    getStudioLutFilters,
    resolveStudioLutVersions,
    getTemplatesFolders,
    getStudioTemplateFolders,
    getStudioTemplateFolder,
    getStudioTemplateFolderDestinations,
    generateSuggestions,
    getMediaProviderCollections,
    getSavedColors,
    addSavedColor,
    editSavedColor,
    // deleteSavedColor,
    togglePageHidden,
  } = apiCallbacks;

  const pageTemplates = useRef({
    base: [],
    withoutImages: [],
  });

  const actions = { getTaxonomyTerm, createTaxonomyTerm };

  actions.getPageTemplates = useCallback(
    async ({ showImages = false } = {}) => {
      // check if pageTemplates have been loaded yet
      if (pageTemplates.current.base.length === 0) {
        pageTemplates.current.base = await getAllTemplates({ cdnURL });
        pageTemplates.current.withoutImages = removeImagesFromPageTemplates(
          pageTemplates.current.base
        );
      }

      return pageTemplates.current[showImages ? 'base' : 'withoutImages'];
    },
    [cdnURL]
  );

  actions.getStoryById = useCallback(
    (storyId) => getStoryById(storyId, stories),
    [stories, getStoryById]
  );

  actions.getDemoStoryById = useCallback(
    (storyId) => getDemoStoryById(storyId, stories),
    [stories, getDemoStoryById]
  );

  actions.saveStoryById = useCallback(
    (story) => saveStoryById(story, stories, encodeMarkup),
    [stories, encodeMarkup, saveStoryById]
  );

  actions.savePages = useCallback((props) => savePages(props), [savePages]);

  actions.autoSaveById = useCallback(
    (story) => autoSaveById(story, stories, encodeMarkup),
    [stories, encodeMarkup, autoSaveById]
  );

  actions.getMedia = useCallback(
    ({ mediaType, searchTerm, pagingNum, cacheBust }) =>
      getMedia({ mediaType, searchTerm, pagingNum, cacheBust }, media),
    [media, getMedia]
  );

  actions.uploadMedia = useCallback(
    (correlationId, file, additionalData, updateProgress) =>
      uploadMedia(correlationId, file, additionalData, media, updateProgress),
    [media, uploadMedia]
  );

  actions.uploadMediaFromBase64String = useCallback(
    (correlationId, resource, updateProgress) =>
      uploadMediaFromBase64String(correlationId, resource, updateProgress),
    [uploadMediaFromBase64String]
  );

  actions.updateMedia = useCallback(
    (mediaId, data) => updateMedia(mediaId, data, media),
    [media, updateMedia]
  );

  actions.deleteMedia = useCallback(
    (mediaId) => deleteMedia(mediaId, media),
    [media, deleteMedia]
  );

  actions.getHotlinkInfo = useCallback(
    (url) => getHotlinkInfo(url, hotlink),
    [hotlink, getHotlinkInfo]
  );

  actions.getLinkMetadata = useCallback(
    (url) => getLinkMetadata(url, link),
    [link, getLinkMetadata]
  );

  actions.getAuthors = useCallback(
    (search = null) => getAuthors(search, users),
    [users, getAuthors]
  );

  actions.getCurrentUser = useCallback(
    () => getCurrentUser(currentUser),
    [currentUser, getCurrentUser]
  );

  actions.updateCurrentUser = useCallback(
    (data) => updateCurrentUser(data, currentUser),
    [currentUser, updateCurrentUser]
  );

  actions.getCustomPageTemplates = useCallback(
    (
      page = 1,
      pageSize = 16,
      folderName = '',
      searchText = '',
      signal,
      assetType = null,
      folderId = null,
      includeDescendants = false
    ) =>
      getCustomPageTemplates(
        page,
        pageSize,
        folderName,
        searchText,
        signal,
        assetType,
        folderId,
        includeDescendants
      ),
    [getCustomPageTemplates]
  );

  actions.getTemplatesFolders = useCallback(
    ({ pageNumber = 1, pageSize = 16, searchText = '', assetType } = {}) => {
      return getTemplatesFolders(pageNumber, pageSize, searchText, assetType);
    },
    [customPageTemplates, getTemplatesFolders]
  );

  actions.getStudioTemplateFolders = useCallback(
    (params, signal) => getStudioTemplateFolders(params, signal),
    [getStudioTemplateFolders]
  );

  actions.getStudioTemplateFolder = useCallback(
    (folderId, signal) => getStudioTemplateFolder(folderId, signal),
    [getStudioTemplateFolder]
  );

  actions.getStudioTemplateFolderDestinations = useCallback(
    (signal) => getStudioTemplateFolderDestinations(signal),
    [getStudioTemplateFolderDestinations]
  );

  actions.generateSuggestions = useCallback(
    (textElements) => {
      return generateSuggestions(textElements);
    },
    [generateSuggestions]
  );

  actions.getSavedColors = useCallback(
    (params) => getSavedColors(params),
    [getSavedColors]
  );

  actions.addSavedColor = useCallback(
    (color) => addSavedColor(color),
    [addSavedColor]
  );

  actions.editSavedColor = useCallback(
    (color) => editSavedColor(color),
    [editSavedColor]
  );

  // actions.deleteSavedColor = useCallback(
  //   (color) => deleteSavedColor(color),
  //   [deleteSavedColor]
  // );

  actions.togglePageHidden = useCallback(
    ({ pageId, isHidden }) => togglePageHidden({ pageId, isHidden }),
    [togglePageHidden]
  );

  actions.getMediaProviderCollections = useCallback(
    ({ mediaProviderId, searchTerm, folder }) => {
      return getMediaProviderCollections({
        mediaProviderId,
        searchTerm,
        folder,
      });
    },
    [getMediaProviderCollections]
  );

  actions.addPageTemplate = useCallback(
    (page) => addPageTemplate(page, customPageTemplates),
    [customPageTemplates, addPageTemplate]
  );

  actions.deletePageTemplate = useCallback(
    (id, assetType) => deletePageTemplate(id, assetType),
    [deletePageTemplate]
  );

  actions.updatePageTemplateMetadata = useCallback(
    (id, input) => updatePageTemplateMetadata(id, input),
    [updatePageTemplateMetadata]
  );

  actions.updatePageTemplateOrder = useCallback(
    (id, input) => updatePageTemplateOrder(id, input),
    [updatePageTemplateOrder]
  );

  actions.updatePageTemplatePin = useCallback(
    (id, input) => updatePageTemplatePin(id, input),
    [updatePageTemplatePin]
  );

  actions.movePageTemplate = useCallback(
    (id, input) => movePageTemplate(id, input),
    [movePageTemplate]
  );

  actions.getTaxonomies = useCallback(
    () => getTaxonomies(postType, taxonomies),
    [postType, taxonomies, getTaxonomies]
  );

  actions.getMediaDetailsFromProvider = useCallback(
    (providerId, assetId, signal) =>
      getMediaDetailsFromProvider(providerId, assetId, signal),
    [getMediaDetailsFromProvider]
  );
  actions.getMediaFromProvider = useCallback(
    (mediaProviderId, params) => getMediaFromProvider(mediaProviderId, params),
    [getMediaFromProvider]
  );

  actions.registerUsage = useCallback(
    (mediaProviderId, params) => registerUsage(mediaProviderId, params),
    [registerUsage]
  );

  actions.download3pAsset = useCallback(
    (mediaProviderId, params) => download3pAsset(mediaProviderId, params),
    [download3pAsset]
  );

  actions.getExternalAssets = useCallback(
    (params, signal) => getExternalAssets(params, signal),
    [getExternalAssets]
  );

  actions.deleteExternalAsset = useCallback(
    (mediaId) => deleteExternalAsset(mediaId),
    [media, deleteMedia]
  );

  actions.getFonts = useCallback((params) => getFonts(params), [getFonts]);

  actions.getShapes = useCallback((params) => getShapes(params), [getShapes]);

  actions.getStickers = useCallback(
    (params) => getStickers(params),
    [getStickers]
  );

  actions.getTextSets = useCallback(
    (params) => getTextSets(params),
    [getTextSets]
  );

  actions.getStudioAnimations = useCallback(
    (params) => getStudioAnimations(params),
    [getStudioAnimations]
  );

  actions.getStudioAnimation = useCallback(
    (animationId) => getStudioAnimation(animationId),
    [getStudioAnimation]
  );

  actions.getStudioLutFilters = useCallback(
    (params) => getStudioLutFilters(params),
    [getStudioLutFilters]
  );

  actions.resolveStudioLutVersions = useCallback(
    (versionIds, params) => resolveStudioLutVersions(versionIds, params),
    [resolveStudioLutVersions]
  );

  // If some api callbacks have not been provided via configuration
  // set those actions as undefined, so we can stop them conditionally.
  // @todo Handle undefined api callbacks where they have been used.
  Object.keys(actions).forEach((name) => {
    if ('getPageTemplates' !== name && !apiCallbacks[name]) {
      actions[name] = undefined;
    }
  });

  return <Context.Provider value={{ actions }}>{children}</Context.Provider>;
}

APIProvider.propTypes = {
  children: PropTypes.node,
};

export default APIProvider;
